<template>
  <Primitive
    :as="as"
    :class="
      cn(
        variants({
          variant,
          size,
          color,
          icon: icon,
          number,
          as,
          type,
          border,
          disabled,
        }),
        props.class
      )
    "
    :title="ariaHidden ? '' : title || label"
    v-on="disabled ? { click: (e) => e.stopPropagation() } : {}"
  >
    <template v-if="loading">
      <LoadingSpinner variant="primary" />
    </template>
    <slot v-else name="content">
      <template v-if="icon && !trailingIcon">
        <slot>
          <Icon :name="iconName" :size="size" @click="$emit(iconEvent)" />
        </slot>
      </template>
      <div
        v-if="(label || $slots.label) && icon !== 'only'"
        class="w-full flex items-center justify-center overflow-hidden"
      >
        <slot name="label">
          <span
            class="font-medium text-center truncate"
            :title="ariaHidden ? '' : title || label"
          >
            {{ label }}
          </span>
        </slot>
      </div>
      <template v-if="trailingIcon">
        <slot>
          <Icon :name="iconName" :size="size" @click="$emit(iconEvent)" />
        </slot>
      </template>
    </slot>
  </Primitive>
</template>

<script setup>
import { computed } from "vue";
import { Primitive } from "radix-vue";
import { Icon } from "@/SharedComponents/Icon";
import {
  types,
  colors,
  sizes,
  icons,
  variants,
} from "@/SharedComponents/Badges";
import { LoadingSpinner } from "@/SharedComponents/LoadingSpinner";
import { cn } from "@/Utils/cn";

defineEmits(["iconClicked", "remove"]);

const props = defineProps({
  label: { type: [String, Number], required: false },
  variant: { type: String, required: false },
  type: {
    type: String,
    validator: (val) => types.includes(val),
    required: false,
  },
  border: { type: String, required: false },
  color: {
    type: String,
    validator: (val) => colors.includes(val),
    required: false,
  },
  size: {
    type: String,
    validator: (val) => sizes.includes(val),
    required: false,
  },
  icon: {
    type: [Boolean, String],
    validator: (val) => icons.includes(val),
    default: false,
  },
  number: { type: Boolean, required: false },
  as: { type: String, default: "div" },
  ariaHidden: { type: Boolean, default: false },
  iconName: { type: String, default: "placeholder" },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  title: { type: String, default: null },
});

const iconName = computed(() => {
  switch (props.icon) {
    case "close":
      return "close";
    case "dot":
      return "dot";
    default:
      return props.iconName;
  }
});

const iconEvent = computed(() => {
  switch (props.icon) {
    case "close":
      return "remove";
    default:
      return "iconClicked";
  }
});

const trailingIcon = computed(() => ["close", "trailing"].includes(props.icon));
</script>
